import { useEffect, type RefObject } from 'react'

// MPD-132 — a sticky bar covers whatever is scrolled to just below it. This publishes the bar's
// height as `--sticky-offset` on the bar's parent, so every chart section and anchor under it
// (App.css: scroll-margin-top) stops below the bar when scrolled into view. 0 while the bar is
// not sticky (the phone layout makes it static), so nothing gets an empty gap there.
export function stickyOffset(bar: HTMLElement): number {
  return getComputedStyle(bar).position === 'sticky' ? Math.ceil(bar.getBoundingClientRect().height) : 0
}

export function useStickyOffset(ref: RefObject<HTMLElement | null>, active = true): void {
  useEffect(() => {
    const bar = ref.current
    const scope = bar?.parentElement
    if (!active || !bar || !scope) return
    const update = () => scope.style.setProperty('--sticky-offset', `${stickyOffset(bar)}px`)
    update()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update)
    observer?.observe(bar)
    // The breakpoint that turns the bar static does not always resize it.
    window.addEventListener('resize', update)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', update)
      scope.style.removeProperty('--sticky-offset')
    }
  }, [ref, active])
}
